import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  TextField,
  Button,
  Collapse,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  SendOutlined as SendOutlinedIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Post } from '../../types/social';
import { SharePostDialog } from './SharePostDialog';
import { useNavigate } from 'react-router-dom';
import { useRelativeTime, formatRelativeTime } from '../../hooks/useRelativeTime';

interface PostCardProps {
  post: Post;
  onLike: (postId: string, isLiked: boolean) => Promise<void>;
  onFavorite: (postId: string, isFavorited: boolean) => Promise<void>;
  onComment: (postId: string, content: string) => Promise<void>;
  onDelete?: (postId: string) => Promise<void>;
  checkLike: (postId: string) => Promise<boolean>;
  checkFavorite: (postId: string) => Promise<boolean>;
  getComments: (postId: string) => Promise<any[]>;
}

export const PostCard: React.FC<PostCardProps> = ({
  post,
  onLike,
  onFavorite,
  onComment,
  onDelete,
  checkLike,
  checkFavorite,
  getComments,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    checkLike(post.id).then(setIsLiked);
    checkFavorite(post.id).then(setIsFavorited);
  }, [post.id]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLike = async () => {
    await onLike(post.id, isLiked);
    setIsLiked(!isLiked);
  };

  const handleFavorite = async () => {
    await onFavorite(post.id, isFavorited);
    setIsFavorited(!isFavorited);
  };

  const handleShowComments = async () => {
    if (!showComments) {
      const fetchedComments = await getComments(post.id);
      setComments(fetchedComments);
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onComment(post.id, commentText);
      setCommentText('');
      const fetchedComments = await getComments(post.id);
      setComments(fetchedComments);
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(post.id);
    }
    handleMenuClose();
  };

  const timeAgo = useRelativeTime(post.createdAt);
  const isOwnPost = user?.uid === post.userId;

  // Display full name if available, otherwise use userName
  const displayName = post.userFirstName && post.userLastName
    ? `${post.userFirstName} ${post.userLastName}`.trim()
    : post.userFirstName || post.userName;

  return (
    <Card
      sx={{
        backgroundColor: theme.colors.card,
        boxShadow: 'none',
        borderRadius: 0,
        borderBottom: `8px solid ${theme.colors.background}`,
      }}
    >
      {/* Header - Instagram style */}
      <CardHeader
        avatar={
          <Avatar
            src={post.userAvatar}
            onClick={() => navigate(`/hostlyclub/profile/${post.userId}`)}
            sx={{
              backgroundColor: theme.colors.primary,
              width: 40,
              height: 40,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {displayName?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
        }
        action={
          <IconButton onClick={handleMenuOpen} sx={{ color: theme.colors.text.primary }}>
            <MoreVertIcon />
          </IconButton>
        }
        title={
          <Typography
            variant="subtitle2"
            onClick={() => navigate(`/hostlyclub/profile/${post.userId}`)}
            sx={{
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {displayName}
            {post.userName && post.userName !== displayName && (
              <Typography component="span" variant="caption" sx={{ color: theme.colors.text.secondary, ml: 1, fontWeight: 'normal' }}>
                @{post.userName}
              </Typography>
            )}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
            {timeAgo}
          </Typography>
        }
        sx={{ pb: 1 }}
      />

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {isOwnPost && onDelete && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon sx={{ mr: 1 }} /> Deletar
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate('/contact');
        }}>Reportar</MenuItem>
      </Menu>

      {/* Image - Full width Instagram style */}
      {post.imageUrl && (
        <Box
          component="img"
          src={post.imageUrl}
          sx={{
            width: '100%',
            maxHeight: 500,
            objectFit: 'cover',
          }}
        />
      )}

      {/* Actions - Instagram style */}
      <CardActions disableSpacing sx={{ padding: '8px 16px', pt: 1 }}>
        <IconButton onClick={handleLike} sx={{ color: isLiked ? '#e91e63' : theme.colors.text.primary, p: 0.5 }}>
          {isLiked ? <FavoriteIcon sx={{ fontSize: 28 }} /> : <FavoriteBorderIcon sx={{ fontSize: 28 }} />}
        </IconButton>
        <IconButton onClick={handleShowComments} sx={{ color: theme.colors.text.primary, p: 0.5, ml: 1 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <IconButton
          onClick={() => setShareDialogOpen(true)}
          sx={{ color: theme.colors.text.primary, p: 0.5, ml: 1 }}
        >
          <SendOutlinedIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={handleFavorite} sx={{ color: isFavorited ? theme.colors.primary : theme.colors.text.primary, p: 0.5 }}>
          {isFavorited ? <BookmarkIcon sx={{ fontSize: 28 }} /> : <BookmarkBorderIcon sx={{ fontSize: 28 }} />}
        </IconButton>
      </CardActions>

      {/* Likes and Favorites count */}
      <Box sx={{ px: 2, pb: 0.5 }}>
        <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 'bold' }}>
          {post.likesCount} {post.likesCount === 1 ? 'curtida' : 'curtidas'}
        </Typography>
        {post.favoritesCount > 0 && (
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
            {post.favoritesCount} {post.favoritesCount === 1 ? 'favorito' : 'favoritos'}
          </Typography>
        )}
      </Box>

      {/* Content */}
      <CardContent sx={{ paddingTop: 1, pb: 1 }}>
        <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
          <b>{displayName}</b> {post.content}
        </Typography>

        {post.eventName && (
          <Box
            sx={{
              p: 1,
              backgroundColor: theme.colors.secondary,
              borderRadius: theme.radii.small,
              mt: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.colors.primary, fontWeight: 'bold' }}>
              📅 Evento: {post.eventName}
            </Typography>
          </Box>
        )}

        {post.recipeName && (
          <Box
            sx={{
              p: 1,
              backgroundColor: theme.colors.secondary,
              borderRadius: theme.radii.small,
              mt: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.colors.primary, fontWeight: 'bold' }}>
              🍽️ Receita: {post.recipeName}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Comments Section */}
      {post.commentsCount > 0 && !showComments && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: theme.colors.text.secondary, cursor: 'pointer' }}
            onClick={handleShowComments}
          >
            Ver {post.commentsCount} {post.commentsCount === 1 ? 'comentário' : 'comentários'}
          </Typography>
        </Box>
      )}

      <Collapse in={showComments}>
        <Divider />
        <Box sx={{ px: 2, py: 1, maxHeight: 300, overflowY: 'auto' }}>
          {comments.map((comment) => (
            <Box key={comment.id} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
                <b>{comment.userName}</b> {comment.content}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                {formatRelativeTime(comment.createdAt)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Divider />
      </Collapse>

      {/* Add Comment */}
      <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Adicione um comentário..."
          value={commentText}
          disabled={isSubmitting}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleAddComment();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: theme.colors.text.primary,
              backgroundColor: theme.colors.background,
              borderRadius: theme.radii.medium,
            },
          }}
        />
        <Button
          variant="text"
          onClick={handleAddComment}
          disabled={!commentText.trim() || isSubmitting}
          sx={{
            color: theme.colors.primary,
            fontWeight: 'bold',
            textTransform: 'none',
            '&:disabled': {
              color: theme.colors.text.secondary,
            },
          }}
        >
          {isSubmitting ? '...' : 'Publicar'}
        </Button>
      </Box>

      <SharePostDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        postId={post.id}
      />
    </Card>
  );
};
